/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { CircularProgress, H2 } from '@ouster/webviz-react-components';

import { Box } from '@mui/material';
import { useEffect } from 'react';

import InviteMember from '../components/organization/InviteMember';
import OrganizationMembers from '../components/organization/OrganizationMembers';
import OrganizationName from '../components/organization/OrganizationName';
import OrganizationSpaceUsage from '../components/organization/OrganizationSpaceUsage';
import useChakraHook from '../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../reducers';
import type { UserRole, UserWithRole } from '../types';

export default function OrganizationManagement() {
  const { curUserOrg, user, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );
  const { fetchUserOrgs } = useStoreActions((actions) => actions.user);
  const { orgUserList, orgInviteeList, orgSpaceUsage } = useStoreState(
    (storeState) => storeState.organization
  );

  const { fetchOrgSpaceUsage } = useStoreActions(
    (actions) => actions.organization
  );

  const {
    updateOrgName,
    fetchOrgUserList,
    fetchOrgInvitationList,
    inviteUserToOrg,
    reinviteUserToOrg,
    updateUserRole,
    deleteUserFromOrg,
    deleteInvitedUserFromOrg,
  } = useStoreActions((actions) => actions.organization);

  const { showToast } = useChakraHook();

  useEffect(() => {
    if (curUserOrg?.uuid && curUserOrg?.id) {
      fetchOrgUserList(curUserOrg.id);
      fetchOrgInvitationList(curUserOrg.uuid);
      fetchOrgSpaceUsage(curUserOrg.id);
    }
  }, [
    fetchOrgUserList,
    fetchOrgInvitationList,
    fetchOrgSpaceUsage,
    curUserOrg?.uuid,
    curUserOrg?.id,
  ]);

  const curUser = orgUserList.find(
    (orgUser: { identity_id: any }) => orgUser.identity_id === user?.identity_id
  );

  const hasPermission = curUserOrg?.role === 'ADMIN';

  const computedOrgUserList = orgUserList.concat(orgInviteeList);

  const findExistedUser = (email: string) =>
    computedOrgUserList.find(
      (existingUser: { email: string }) =>
        existingUser.email?.toLowerCase() === email.toLowerCase()
    );

  async function inviteHandler(
    { emails, role }: { emails: string[]; role: UserRole },
    onSuccess: () => void
  ) {
    if (!curUserOrg?.id) return;

    const totalCount = emails.length;

    // Process each email
    const results = await Promise.allSettled(
      emails.map(async (email) => {
        // check whether the user existed, if yes, resent invitation instead of starting a fresh invitation
        const existedUser = findExistedUser(email);
        if (existedUser && existedUser.invitation_link) {
          await reinviteUserToOrg(existedUser.invitation_link);
          return { email, success: true };
        }
        await inviteUserToOrg({ email, role, organization_id: curUserOrg.id });
        return { email, success: true };
      })
    );

    // Count successes and failures
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failCount = results.filter((r) => r.status === 'rejected').length;
    const errors = results
      .map((r, index) =>
        r.status === 'rejected' ? `${emails[index]}: ${r.reason}` : null
      )
      .filter((error): error is string => error !== null);

    // Show consolidated notification
    if (successCount > 0) {
      if (totalCount === 1) {
        showToast({
          title: 'Invitation Sent',
          description: 'An email invitation has been sent to the invitee.',
          status: 'success',
        });
      } else {
        showToast({
          title: `${successCount} Invitation${
            successCount > 1 ? 's' : ''
          } Sent`,
          description: `${successCount} out of ${totalCount} invitation${
            totalCount > 1 ? 's were' : ' was'
          } successfully sent.`,
          status: 'success',
        });
      }
    }

    if (failCount > 0) {
      showToast({
        title: `Failed to invite ${failCount} user${failCount > 1 ? 's' : ''}`,
        description: errors.join('\n'),
        status: 'error',
      });
    }

    // Reset form if at least one invitation was successful
    if (successCount > 0) {
      onSuccess();
      /* 
        Fetch both lists, invited can already be the app user
        If that is the case, the user will be added automatically instead of sending invitation
      */
      fetchOrgUserList(curUserOrg.id);
      fetchOrgInvitationList(curUserOrg.uuid);
    }
  }

  async function reinviteHandler(invitation_link: string) {
    try {
      await reinviteUserToOrg(invitation_link);
      showToast({
        title: 'Invitation Sent',
        description: 'An email invitation has been resent to the invitee.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to invite user',
        description: `${error}`,
        status: 'error',
      });
    }
  }

  async function deleteUserHandler(member: UserWithRole) {
    try {
      if (member.invitation_status === 'CREATED') {
        if (!curUserOrg?.uuid || !member.uuid) return;
        await deleteInvitedUserFromOrg({
          organization_uuid: curUserOrg.uuid,
          invitation_uuid: member.uuid,
        });
        await fetchOrgInvitationList(curUserOrg.uuid);
      } else {
        await deleteUserFromOrg(member);
        if (curUserOrg?.id) await fetchOrgUserList(curUserOrg.id);
      }

      showToast({
        title: 'User deleted',
        description: 'The user has been successfully deleted.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        description: `Unable to delete the user. ${error}`,
        status: 'error',
      });
    }
  }

  async function updateUserRoleHandler(member: UserWithRole, role: UserRole) {
    try {
      await updateUserRole({ ...member, role });
      if (curUserOrg?.id) await fetchOrgUserList(curUserOrg.id);

      showToast({
        title: 'User role updated',
        description: 'The user role has been successfully updated.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        description: `Unable to update user role. ${error}`,
        status: 'error',
      });
    }
  }

  async function updateOrgNameHandler(name: string) {
    if (!curUserOrg?.id) return;
    try {
      await updateOrgName({
        organization_id: curUserOrg.id,
        name,
      });
      /* 
        no need to await here, we will fetch a new list with orgs user belong to, getting the updated org name.
      */
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fetchUserOrgs(user!.identity_id);
      showToast({
        title: 'Organization name updated',
        description: `The organization name has been successfully updated to ${name}.`,
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        description: `Unable to update organization name role. ${error}`,
        status: 'error',
      });
    }
  }

  if (!curUserOrg || !curUser) {
    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="org-mgmt-loading"
      >
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </Box>
    );
  }

  return (
    <Box
      height="100%"
      alignContent="start"
      rowGap="2rem"
      display="grid"
      p="30px 26px 36px"
      data-testid="org-mgmt-root"
    >
      <H2>Organization Management</H2>

      <OrganizationName
        curUserOrg={curUserOrg}
        updateOrgNameHandler={updateOrgNameHandler}
        key={curUserOrg.name}
      />
      {/* 
        we will hide the usage when
        - without limitation, allowed_space = -1
        - hasn't fetched the actual data , allowed_space = 0
      */}
      {orgSpaceUsage.allowed_space > 0 && (
        <OrganizationSpaceUsage orgSpaceUsage={orgSpaceUsage} />
      )}

      {hasPermission && (
        <InviteMember
          inviteHandler={inviteHandler}
          isOusterUser={isOusterUser}
          is_provisioned={curUserOrg.is_provisioned}
        />
      )}

      <OrganizationMembers
        isAdmin={hasPermission}
        orgUserList={computedOrgUserList}
        curUser={curUser}
        deleteUserHandler={deleteUserHandler}
        updateUserRoleHandler={updateUserRoleHandler}
        reinviteHandler={reinviteHandler}
        isOusterUser={isOusterUser}
        isProvisioned={curUserOrg.is_provisioned}
      />
    </Box>
  );
}
